import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Points de fidélité (scopés au commerçant).
// - `pointtransactions` : registre des mouvements (source de vérité).
//     Contrainte base : `type` ∈ { 'earn' | 'redeem' | 'transfer' }.
// - `pointfidelite`     : solde agrégé (colonnes NOT NULL obligatoires :
//     nombre, totalttc, iduser, idcommerce, idcommande, typepoint, date, raison).
// ─────────────────────────────────────────────────────────────

/** Solde de points de fidélité d'un client chez un commerçant (calculé depuis le registre). */
export async function getLoyaltyPoints(merchantId: number, clientUserId: number): Promise<number> {
  const { data, error } = await supabase
    .from('pointtransactions')
    .select('type, points')
    .eq('idcommerce', merchantId)
    .eq('user_id', clientUserId);
  if (error || !data) return 0;
  return (data as { type: string; points: number }[]).reduce((sum, t) => {
    const p = Math.abs(Number(t.points) || 0);
    if (t.type === 'earn') return sum + p;
    if (t.type === 'redeem' || t.type === 'transfer') return sum - p;
    return sum;
  }, 0);
}

export interface AddPointsResult {
  success: boolean;
  newTotal?: number;
  error?: string;
}

/**
 * Ajoute (ou retire si négatif) des points de fidélité à un client.
 * Enregistre le mouvement dans `pointtransactions` (registre officiel) puis
 * met à jour le solde agrégé `pointfidelite` (best-effort), et notifie le client.
 */
export async function addLoyaltyPoints(input: {
  merchantId: number;
  clientUserId: number;
  points: number;
  description?: string;
  actorId?: number | null;
  actorName?: string;
}): Promise<AddPointsResult> {
  const { merchantId, clientUserId, points, description, actorId, actorName } = input;
  const delta = Math.trunc(Number(points));
  if (!Number.isFinite(delta) || delta === 0) {
    return { success: false, error: 'Nombre de points invalide.' };
  }

  const current = await getLoyaltyPoints(merchantId, clientUserId);
  const newTotal = Math.max(0, current + delta);

  // 1. Registre des mouvements — `type` doit être une valeur acceptée par la base.
  const { error: histErr } = await supabase.from('pointtransactions').insert({
    user_id: clientUserId,
    idcommerce: merchantId,
    type: delta > 0 ? 'earn' : 'redeem',
    points: Math.abs(delta),
    description:
      description ||
      (delta > 0
        ? `Points ajoutés${actorName ? ` par ${actorName}` : ''}`
        : `Points retirés${actorName ? ` par ${actorName}` : ''}`),
  });
  if (histErr) return { success: false, error: histErr.message };

  // 2. Solde agrégé (best-effort : ne doit jamais bloquer l'opération principale).
  try {
    const { data: existing } = await supabase
      .from('pointfidelite')
      .select('id')
      .eq('idcommerce', merchantId)
      .eq('iduser', clientUserId)
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing?.id) {
      await supabase
        .from('pointfidelite')
        .update({
          nombre: String(newTotal),
          date: new Date().toISOString(),
          raison: description || (delta > 0 ? 'Points ajoutés' : 'Points retirés'),
        })
        .eq('id', existing.id);
    } else {
      await supabase.from('pointfidelite').insert({
        iduser: clientUserId,
        idcommerce: merchantId,
        nombre: String(newTotal),
        totalttc: 0,
        idcommande: 0,
        typepoint: delta > 0 ? 1 : 2,
        date: new Date().toISOString(),
        raison: description || (delta > 0 ? 'Points ajoutés' : 'Points retirés'),
      });
    }
  } catch {
    /* solde agrégé best-effort — le registre reste la source de vérité */
  }

  // 3. Notification du client (best-effort)
  try {
    await supabase.from('notifications').insert({
      user_id: clientUserId,
      sender_id: actorId ?? merchantId,
      publication_id: 0,
      type: 'loyalty_points',
      message: delta > 0 ? `+${delta} points de fidélité` : `${delta} points de fidélité`,
      is_read: 0,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('notifications-changed'));
    }
  } catch {
    /* notification best-effort */
  }

  return { success: true, newTotal };
}